import type { GameSnapshot } from "../engine/core/types";
import { BIG_WAVE_LEAD } from "../mechanics/spawning/spawnWave";

/** 时间轴的一段：关卡装载时由 Game 给一次。 */
export interface TimelineSegment {
  at: number;
  duration: number;
  rate: number;
  big: boolean;
  note: string;
}

export interface HudActions {
  onPause(): void;
  onRestart(): void;
  onNext(): void;
  onMute(): boolean;
  onFeedback(rating: number): boolean;
}

/** 开局插卡的显示时长（秒，按关卡时间）。 */
const INTRO_SECONDS = 2.5;
/** 采集告知：试玩期间全程记录会话，暂停与结束卡片都说一遍。 */
const RECORDING_NOTE = "Play sessions are recorded\nto help tune the game.";
/** 卡片窄，操作说明拆两行；开局插卡是宽的，仍用一行。 */
const CONTROLS_NOTE = "Move with mouse or finger\nHold to spray";
/** 第一关多留一点时间给操作说明。 */
const FIRST_INTRO_SECONDS = 4;

/**
 * DOM HUD 的状态同步（决策 0018：HUD 是 DOM 叠层，不画进画布）。
 * 常驻只有三样：失误标记、按钮组、关卡进度；关卡名与目标是开局插卡，到时自动消失。
 * 波段不弹字：普通波段靠进度条上的旗，大波只有橘色横幅。借鉴的是 Fruit Ninja 的红叉与 PvZ 的进度条：不要标签、不要数字。
 * `GameSnapshot` 里的 score／combo 仍在（进记录协议，归 T057），这里不再读取。
 */
export class HudController {
  private readonly intro = this.get('[data-hud="intro"]');
  private readonly kicker = this.get('[data-hud="kicker"]');
  private readonly level = this.get('[data-hud="level"]');
  private readonly objective = this.get('[data-hud="objective"]');
  private readonly introRule = this.get('[data-hud="intro-rule"]');
  private readonly introHint = this.get('[data-hud="intro-hint"]');
  private readonly leaked = this.get('[data-hud="leaked"]');
  private readonly strikes = this.get('[data-hud="leak-pips"]');
  private readonly progress = this.get('[data-hud="timeline"]');
  private readonly progressFill = this.get('[data-hud="timeline-fill"]');
  private readonly progressFlags = this.get('[data-hud="timeline-track"]');
  private readonly progressMarker = this.get('[data-hud="timeline-head"]');
  private readonly waveBanner = this.get('[data-hud="wave-banner"]');
  private readonly frame = this.get("[data-stage-frame]");
  private readonly overlay = this.get('[data-hud="overlay"]');
  private readonly overlayEyebrow = this.get('[data-hud="overlay-eyebrow"]');
  private readonly overlayTitle = this.get('[data-hud="overlay-title"]');
  private readonly overlayBody = this.get('[data-hud="overlay-body"]');
  private readonly overlayNote = this.get('[data-hud="overlay-note"]');
  private readonly nextButton = this.get(
    '[data-action="next"]',
  ) as HTMLButtonElement;
  private readonly pauseButton = this.get(
    '[data-action="pause"]',
  ) as HTMLButtonElement;
  private readonly muteButton = this.get(
    '[data-action="mute"]',
  ) as HTMLButtonElement;
  private readonly feedback = this.get(
    '[data-hud="feedback"]',
  ) as HTMLFormElement;
  private readonly feedbackSubmit = this.get(
    '[data-action="feedback-submit"]',
  ) as HTMLButtonElement;
  private readonly feedbackStatus = this.get('[data-hud="feedback-status"]');
  private feedbackSubmitted = false;
  private segments: TimelineSegment[] = [];
  private flagNodes: HTMLElement[] = [];
  private timelineEnd = 0;
  private lastLeaked = 0;
  private strikeLimit = -1;
  private flashTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly lifetime = new AbortController();
  destroy() {
    this.lifetime.abort();
    clearTimeout(this.flashTimer);
  }

  constructor(actions: HudActions) {
    this.feedback.addEventListener(
      "change",
      () => {
        this.feedbackSubmit.disabled =
          this.feedbackSubmitted || !this.feedback.checkValidity();
      },
      { signal: this.lifetime.signal },
    );
    this.feedback.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        if (
          this.feedback.hidden ||
          this.feedbackSubmitted ||
          !this.feedback.checkValidity()
        )
          return;
        const rating = Number(new FormData(this.feedback).get("rating"));
        if (!actions.onFeedback(rating)) {
          this.feedbackStatus.textContent =
            "Feedback is unavailable right now. Please try again.";
          return;
        }
        this.feedbackSubmitted = true;
        this.feedbackSubmit.disabled = true;
        this.feedback.querySelector("fieldset")!.disabled = true;
        this.feedbackStatus.textContent = "Thanks for your feedback!";
      },
      { signal: this.lifetime.signal },
    );
    this.get('[data-action="restart"]').addEventListener(
      "click",
      actions.onRestart,
      { signal: this.lifetime.signal },
    );
    this.nextButton.addEventListener("click", actions.onNext, {
      signal: this.lifetime.signal,
    });
    this.pauseButton.addEventListener("click", actions.onPause, {
      signal: this.lifetime.signal,
    });
    this.muteButton.addEventListener(
      "click",
      () => {
        const muted = actions.onMute();
        this.muteButton.textContent = muted ? "♪̸" : "♪";
        this.muteButton.title = muted ? "Sound: off" : "Sound: on";
        this.muteButton.setAttribute("aria-pressed", String(muted));
      },
      { signal: this.lifetime.signal },
    );
  }

  /** 关卡装载时调用。空表即隐藏进度条（坐标表关卡与测试 Fixture）。 */
  setTimeline(segments: TimelineSegment[]): void {
    this.segments = segments;
    this.lastLeaked = 0;
    this.strikeLimit = -1;
    this.timelineEnd = segments.reduce(
      (end, segment) => Math.max(end, segment.at + segment.duration),
      0,
    );
    this.progress.toggleAttribute(
      "hidden",
      segments.length === 0 || this.timelineEnd <= 0,
    );
    // 旗只给大波立：玩家读的是「大波什么时候到」。普通波段不立旗——六面旗读起来是六次警报，
    // PvZ 的进度条也只标巨大波。节点仍按段一一创建（隐藏），update 按下标对齐。
    this.flagNodes = segments.map((segment) => {
      const node = document.createElement("div");
      node.className = "progress__flag";
      node.title = segment.note;
      node.classList.toggle("is-big", segment.big);
      node.hidden = !segment.big;
      node.style.left = `${(segment.at / this.timelineEnd) * 100}%`;
      return node;
    });
    this.progressFlags.replaceChildren(...this.flagNodes);
    this.waveBanner.hidden = true;
  }

  update(snapshot: GameSnapshot, objectiveText: string, kicker: string): void {
    this.level.textContent = `${String(snapshot.level).padStart(2, "0")} // ${snapshot.levelName}`;
    this.objective.textContent = objectiveText;
    this.kicker.textContent = kicker;
    // 开局插卡：关卡名与目标只在开局 INTRO_SECONDS 内显示，之后不再占位。
    const introSeconds =
      snapshot.level === 1 ? FIRST_INTRO_SECONDS : INTRO_SECONDS;
    this.intro.toggleAttribute(
      "hidden",
      !(snapshot.phase === "playing" && snapshot.levelTime < introSeconds),
    );
    this.leaked.textContent =
      snapshot.leakLimit > 0
        ? `${snapshot.leaked}/${snapshot.leakLimit}`
        : String(snapshot.leaked);
    // 开局提示只在第一关：一句目标加一行操作说明；漏几只由左上的剪影表达，不重复。
    const first = snapshot.level === 1;
    this.introRule.textContent = first ? "HOLD THE LINE" : "";
    this.introRule.hidden = !first;
    // 两行：一行讲移动、一行讲喷水，比中间点隔开的一长串好读；样式用 pre-line 换行。
    this.introHint.textContent = first ? CONTROLS_NOTE : "";
    this.introHint.hidden = !first;
    this.updateProgress(snapshot);
    this.pauseButton.textContent = snapshot.phase === "paused" ? "▶" : "II";
    this.pauseButton.title = snapshot.phase === "paused" ? "Resume" : "Pause";

    const showOverlay =
      snapshot.phase === "level-clear" ||
      snapshot.phase === "game-over" ||
      snapshot.phase === "complete" ||
      snapshot.phase === "paused";
    this.overlay.toggleAttribute("hidden", !showOverlay);
    this.feedback.hidden = snapshot.phase !== "complete";
    this.overlay.classList.toggle("is-complete", snapshot.phase === "complete");
    if (!showOverlay) return;
    let note = "";
    if (snapshot.phase === "game-over") {
      // 与开局的 HOLD THE LINE 呼应：眉题说原因，标题说结果，正文一句陈述，按钮本身就是邀请。
      this.overlayEyebrow.textContent = "TOO MANY GOT PAST";
      this.overlayTitle.textContent = "LINE BROKEN";
      this.overlayBody.textContent = "The slimes got past you.";
      this.nextButton.textContent = "RETRY LEVEL";
    } else if (snapshot.phase === "complete") {
      // 三关试玩结束后收集推荐意愿。
      this.overlayEyebrow.textContent = "DEMO COMPLETE";
      this.overlayTitle.textContent = "THANKS FOR PLAYING!";
      this.overlayBody.textContent =
        "That's all three stages of this playtest build. Every slime cleared.";
      note = RECORDING_NOTE;
      this.nextButton.textContent = "PLAY AGAIN";
    } else if (snapshot.phase === "paused") {
      this.overlayEyebrow.textContent = "SYSTEM HOLD";
      this.overlayTitle.textContent = "PAUSED";
      this.overlayBody.textContent = "Jelly time is on hold.";
      note = `${CONTROLS_NOTE}
${RECORDING_NOTE}`;
      this.nextButton.textContent = "RESUME";
    } else {
      this.overlayEyebrow.textContent = kicker;
      this.overlayTitle.textContent = "STAGE CLEAR";
      this.overlayBody.textContent = `Stage cleared with ${snapshot.chainPops} chain pops.`;
      this.nextButton.textContent = "NEXT STAGE";
    }
    this.overlayNote.textContent = note;
    this.overlayNote.hidden = note === "";
  }

  /** 进度条填充、旗的高亮、大波预告与失误标记。全部由快照现算，只存上次落地数用于闪烁。 */
  private updateProgress(snapshot: GameSnapshot): void {
    if (this.timelineEnd > 0) {
      const t = snapshot.levelTime;
      const percent = `${Math.min(100, (t / this.timelineEnd) * 100)}%`;
      this.progressFill.style.width = percent;
      this.progressMarker.style.left = percent;
      let warning = false;
      this.segments.forEach((segment, index) => {
        const node = this.flagNodes[index];
        node.classList.toggle(
          "is-active",
          t >= segment.at && t < segment.at + segment.duration,
        );
        // 大波预告：起点前 BIG_WAVE_LEAD 秒亮起，起点后 1 秒收起。
        const lead = segment.at - t;
        const warn = segment.big && lead <= BIG_WAVE_LEAD && lead > -1;
        node.classList.toggle("is-warning", warn);
        warning = warning || warn;
      });
      this.waveBanner.hidden = !warning || snapshot.phase !== "playing";
    }
    // 失误标记：上限变了才重建；漏一只亮一个，只剩两个时闪；每次落地内框闪一下。
    if (snapshot.leakLimit !== this.strikeLimit) {
      this.strikeLimit = snapshot.leakLimit;
      this.strikes.replaceChildren(
        ...Array.from({ length: Math.min(12, snapshot.leakLimit) }, () =>
          document.createElement("i"),
        ),
      );
    }
    const strikes = this.strikes.children;
    for (let index = 0; index < strikes.length; index += 1) {
      const filled = index < snapshot.leaked;
      strikes[index].classList.toggle("is-filled", filled);
      strikes[index].classList.toggle(
        "is-last",
        !filled &&
          index >= strikes.length - 2 &&
          snapshot.leaked >= strikes.length - 2,
      );
    }
    if (snapshot.leaked > this.lastLeaked && snapshot.leaked > 0)
      this.flashFrame();
    this.lastLeaked = snapshot.leaked;
  }

  private flashFrame(): void {
    this.frame.classList.add("is-leak-flash");
    if (this.flashTimer !== undefined) clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(
      () => this.frame.classList.remove("is-leak-flash"),
      220,
    );
  }

  private get(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (element === null) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
